import moment from "moment";

// Adds a filter to the filters array if it doesn't already exist, or removes it if it does. Also allows for a specific filter to removed if needed. Clears all filters if no filter is passed.
export const updateFilters = (addFilter, removeFilter, filters, setFilters) => {
  let newFilters = (addFilter || removeFilter) && filters ? [...filters] : [];

  if (addFilter) {
    // Remove any sort filters if a new sort filter is added
    if (addFilter.includes("sort")) {
      newFilters = newFilters.filter((item) => !item.includes("sort"));
    }

    if (addFilter.includes("ticket-event")) {
      newFilters = newFilters.filter((item) => !item.includes("ticket-event"));
    }

    if (newFilters.includes(addFilter)) {
      newFilters = newFilters.filter((item) => item !== addFilter);
    } else {
      newFilters.push(addFilter);
    }
  }

  // Remove a specific filter if needed
  if (removeFilter) {
    if (Array.isArray(removeFilter)) {
      removeFilter.forEach((item) => {
        newFilters = newFilters.filter((filter) => filter !== item);
      });
    } else {
      newFilters = newFilters.filter((item) => item !== removeFilter);
    }
  }

  setFilters(newFilters);
};

// CSS for the filter buttons
export const getFilterSx = (filter, filters, theme) => {
  const filterSx = {
    textTransform: "none",
    fontWeight: "600",
    fontSize: "18px",
    transition: "0.2s ease",
    color:
      filters?.includes(filter) || filter === true
        ? `${theme?.palette?.secondary?.contrastText} !important`
        : `${theme?.palette?.text?.primary} !important`,
    backgroundColor:
      filters?.includes(filter) || filter === true
        ? `${theme?.palette?.secondary?.main} !important`
        : `inherit`,
    "&:hover": {
      backgroundColor:
        filters?.includes(filter) || filter === false
          ? `${theme?.palette?.text?.disabled} !important`
          : `inherit`,
      color:
        filters?.includes(filter) || filter === false
          ? `${theme?.palette?.secondary?.contrastText} !important`
          : `${theme?.palette?.text?.primary} !important`
    }
  };

  return filterSx;
};

export const clearFilterSx = (filter = false, filters, theme) => {
  const filterSx = {
    display: filter || (!filter && filters?.length) ? "flex" : "none",
    textTransform: "none",
    fontWeight: "600",
    fontSize: "18px",
    transition: "0.2s ease",
    color: `${theme?.palette?.text?.primary} !important`,
    backgroundColor: `inherit`,
    "&:hover": {
      backgroundColor: `${theme?.palette?.error?.main} !important`
    }
  };

  return filterSx;
};

// --- /ORGANIZE Pages ---

// Filters and sorts events based on the filters passed in
export const filterAndSortEvents = (events, filters) => {
  /* Filters and sort types should follow this format:
  Filters: `filter-<type>`
  Sort: `sort-<type>`
  Game System: `gamesystem-<gameSystemId>`
  */

  if (events?.length && filters.length) {
    let tempEvents = [...events];

    const sortFilters = [];
    const gsFilters = [];

    filters.forEach((filter) => {
      // Game Systems
      if (filter?.includes("gamesystem-")) {
        const gameSystemId = filter.split("-")[1];

        gsFilters.push(gameSystemId);
      }

      // Sort
      else if (filter?.includes("sort-")) {
        sortFilters.push(filter);
      }

      // Filters
      else if (filter === "filter-online") {
        tempEvents = tempEvents.filter((event) => event.isOnlineEvent);
      } else if (filter === "filter-exclude-online") {
        tempEvents = tempEvents.filter((event) => !event.isOnlineEvent);
      } else if (filter === "filter-singles") {
        tempEvents = tempEvents.filter(
          (event) =>
            !event.doublesEvent && !event.teamEvent && !event.boardGameEvent
        );
      } else if (filter === "filter-doubles") {
        tempEvents = tempEvents.filter((event) => event.doublesEvent);
      } else if (filter === "filter-teams") {
        tempEvents = tempEvents.filter(
          (event) => !event.doublesEvent && event.teamEvent
        );
      } else if (filter === "filter-board") {
        tempEvents = tempEvents.filter((event) => event.boardGameEvent);
      } else if (filter === "filter-org") {
        tempEvents = tempEvents.filter((event) => event.gameStoreId);
      } else if (filter === "filter-exclude-org") {
        tempEvents = tempEvents.filter((event) => !event.gameStoreId);
      }
    });

    // Filters out events by game system
    if (gsFilters?.length) {
      const gSFilteredEvents = [];
      let found = false;

      gsFilters.forEach((gameSystemId) => {
        tempEvents.forEach((event) => {
          if (event.gameSystemId === gameSystemId) {
            gSFilteredEvents.push(event);
            found = true;
          }
        });
      });

      tempEvents = found ? gSFilteredEvents : [];
    }

    // Final sort
    if (sortFilters?.length && tempEvents?.length) {
      sortFilters.forEach((filter) => {
        if (filter === "sort-date-asc") {
          tempEvents = tempEvents.sort((a, b) => {
            const dateA = moment(a?.eventDate);
            const dateB = moment(b?.eventDate);

            if (dateA.isBefore(dateB)) {
              return 1;
            }
            if (dateA.isAfter(dateB)) {
              return -1;
            }
            return 0;
          });
        } else if (filter === "sort-date-desc") {
          tempEvents = tempEvents.sort((a, b) => {
            const dateA = moment(a?.eventDate);
            const dateB = moment(b?.eventDate);

            if (dateA.isBefore(dateB)) {
              return -1;
            }
            if (dateA.isAfter(dateB)) {
              return 1;
            }
            return 0;
          });
        } else if (filter === "sort-name-asc") {
          tempEvents = tempEvents.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          );
        } else if (filter === "sort-name-desc") {
          tempEvents = tempEvents.sort((a, b) =>
            b.name.toLowerCase() < a.name.toLowerCase() ? -1 : 1
          );
        } else if (filter === "sort-rounds-asc") {
          tempEvents = tempEvents.sort((a, b) =>
            a?.numberOfRounds < b?.numberOfRounds ? -1 : 1
          );
        } else if (filter === "sort-rounds-desc") {
          tempEvents = tempEvents.sort((a, b) =>
            b?.numberOfRounds < a?.numberOfRounds ? -1 : 1
          );
        }
      });
    }

    return tempEvents;
  } else if (!filters.length) {
    return null;
  }
};

export const filterAndSortGameStores = (gameStores, filters) => {
  if (gameStores?.length && filters.length) {
    let tempGameStores = [...gameStores];

    filters.forEach((filter) => {
      switch (filter) {
        // Filter Types
        case "filter-active":
          tempGameStores = tempGameStores.filter((store) => store.active);
          break;
        case "filter-inactive":
          tempGameStores = tempGameStores.filter((store) => !store.active);
          break;
        case "filter-approved":
          tempGameStores = tempGameStores.filter(
            (store) => store.approvalStatus === "filter-approved"
          );
          break;
        case "filter-not-approved":
          tempGameStores = tempGameStores.filter(
            (store) => store.approvalStatus !== "filter-approved"
          );
          break;

        // Sort Types
        case "sort-a-z":
          tempGameStores = tempGameStores.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          );
          break;
        case "sort-z-a":
          tempGameStores = tempGameStores.sort((a, b) =>
            b.name.toLowerCase() < a.name.toLowerCase() ? -1 : 1
          );
          break;
        case "sort-newest":
          tempGameStores = tempGameStores.sort((a, b) => {
            const dateA = moment(a?.created_at);
            const dateB = moment(b?.created_at);

            if (dateA.isBefore(dateB)) {
              return 1;
            }
            if (dateA.isAfter(dateB)) {
              return -1;
            }
            return 0;
          });
          break;
        case "sort-oldest":
          tempGameStores = tempGameStores.sort((a, b) => {
            const dateA = moment(a?.created_at);
            const dateB = moment(b?.created_at);

            if (dateA.isBefore(dateB)) {
              return -1;
            }
            if (dateA.isAfter(dateB)) {
              return 1;
            }
            return 0;
          });
          break;

        default:
          break;
      }
    });

    return tempGameStores;
  } else if (!filters.length) {
    return null;
  }
};

export const filterAndSortTicketSales = (tickets, filters) => {
  if (tickets.length && filters.length) {
    const filterFilters = filters.filter((item) => item.includes("filter"));
    const sortFilters = filters.filter((item) => item.includes("sort"));

    let tempTickets = [...tickets];
    const filteredTickets = [];

    if (filterFilters?.length) {
      filterFilters.forEach((filter) => {
        if (filter === "filter-completed") {
          filteredTickets.push(
            ...tempTickets.filter(
              (item) => item?.status?.toLowerCase() === "completed charge"
            )
          );
        } else if (filter === "filter-pending") {
          filteredTickets.push(
            ...tempTickets.filter(
              (item) =>
                item?.status?.toLowerCase() === "pending charge" ||
                item?.status?.toLowerCase() === "waiting for charge"
            )
          );
        } else if (filter === "filter-expired") {
          filteredTickets.push(
            ...tempTickets.filter(
              (item) => item?.status?.toLowerCase() === "expired checkout"
            )
          );
        } else if (filter === "filter-refunded") {
          filteredTickets.push(
            ...tempTickets.filter(
              (item) =>
                item?.status?.toLowerCase() === "refunded" ||
                item?.status?.toLowerCase() === "partially refunded"
            )
          );
        }
      });

      tempTickets = filteredTickets.length ? filteredTickets : tempTickets;
    }

    if (sortFilters?.length) {
      sortFilters.forEach((filter) => {
        if (filter === "sort-date-asc") {
          tempTickets = tempTickets.sort((a, b) => {
            const dateA = moment(a?.created_at);
            const dateB = moment(b?.created_at);

            if (dateA.isBefore(dateB)) {
              return -1;
            }
            if (dateA.isAfter(dateB)) {
              return 1;
            }
            return 0;
          });
        } else if (filter === "sort-date-desc") {
          tempTickets = tempTickets.sort((a, b) => {
            const dateA = moment(a?.created_at);
            const dateB = moment(b?.created_at);

            if (dateA.isBefore(dateB)) {
              return 1;
            }
            if (dateA.isAfter(dateB)) {
              return -1;
            }
            return 0;
          });
        } else if (filter === "sort-price-asc") {
          tempTickets = tempTickets.sort((a, b) => a.amount - b.amount);
        } else if (filter === "sort-price-desc") {
          tempTickets = tempTickets.sort((a, b) => b.amount - a.amount);
        } else if (filter === "sort-player-name-asc") {
          tempTickets = tempTickets.sort((a, b) =>
            a.playerName.toLowerCase() < b.playerName.toLowerCase() ? -1 : 1
          );
        } else if (filter === "sort-player-name-desc") {
          tempTickets = tempTickets.sort((a, b) =>
            b.playerName.toLowerCase() < a.playerName.toLowerCase() ? -1 : 1
          );
        } else if (filter === "sort-event-name-asc") {
          tempTickets = tempTickets.sort((a, b) =>
            a.event?.name.toLowerCase() < b.event?.name.toLowerCase() ? -1 : 1
          );
        } else if (filter === "sort-event-name-desc") {
          tempTickets = tempTickets.sort((a, b) =>
            b.event?.name.toLowerCase() < a.event?.name.toLowerCase() ? -1 : 1
          );
        }
      });
    }

    return tempTickets;
  } else return null;
};

// --- /ORGANIZE/ORGANIZATION Pages ---
export const filterAndSortRankings = (leaderboard, filters) => {
  /* Filters and sort types should follow this format:
  Filters: `filter-<type>`
  Sort: `sort-<type>`
  Game System: `gamesystem-<gameSystemId>`
  */

  if (leaderboard?.length && filters.length) {
    let tempLeaderboard = [...leaderboard];

    const sortFilters = [];
    const gsFilters = [];

    filters.forEach((filter) => {
      // Game Systems
      if (filter?.includes("gamesystem-")) {
        const gameSystemId = filter.split("-")[1];

        gsFilters.push(gameSystemId);
      }

      // Sort
      else if (filter?.includes("sort-")) {
        sortFilters.push(filter);
      }

      // Filters
      else if (filter === "filter-active") {
        tempLeaderboard = tempLeaderboard.filter(
          (leaderboard) => leaderboard?.league?.active
        );
      } else if (filter === "filter-exclude-active") {
        tempLeaderboard = tempLeaderboard.filter(
          (leaderboard) => !leaderboard?.league?.active
        );
      } else if (filter === "filter-hobby") {
        tempLeaderboard = tempLeaderboard.filter(
          (leaderboard) => leaderboard?.league?.hobby
        );
      } else if (filter === "filter-exclude-hobby") {
        tempLeaderboard = tempLeaderboard.filter(
          (leaderboard) => !leaderboard?.league?.hobby
        );
      }
    });

    // Filters out leaderboards by game system
    if (gsFilters?.length) {
      const gSFilteredLeaderboards = [];
      let found = false;

      gsFilters.forEach((gameSystemId) => {
        tempLeaderboard.forEach((leaderboard) => {
          if (
            leaderboard?.league?.gameSystemId === gameSystemId ||
            leaderboard?.league?.gameSystem?.id === gameSystemId
          ) {
            gSFilteredLeaderboards.push(leaderboard);
            found = true;
          }
        });
      });

      tempLeaderboard = found ? gSFilteredLeaderboards : [];
    }

    // Final sort
    if (sortFilters?.length && tempLeaderboard?.length) {
      sortFilters.forEach((filter) => {
        if (filter === "sort-name-asc") {
          tempLeaderboard = tempLeaderboard.sort((a, b) =>
            a?.league?.name.toLowerCase() < b?.league?.name.toLowerCase()
              ? -1
              : 1
          );
        } else if (filter === "sort-name-desc") {
          tempLeaderboard = tempLeaderboard.sort((a, b) =>
            b?.league?.name.toLowerCase() < a?.league?.name.toLowerCase()
              ? -1
              : 1
          );
        }
      });
    }

    return tempLeaderboard;
  } else if (!filters.length) {
    return null;
  }
};
